/**
 * Client
 * @module Client
 */

import { logoutUser, ssoExpire } from '../store/actions';
import { toast } from 'react-toastify';
import { store } from '../store/index';

export class ServerError extends Error {
	constructor(message) {
		super(message);

		Error.captureStackTrace(this, ServerError);

		this.name = 'ServerError';

		return this;
	}
}

export function parseError(error) {
	return error || 'Something went wrong';
}

/**
 * Check if an error is a firewall redirect error
 * @param {Error} error - Error object to check
 * @returns {boolean} True if this is a firewall redirect error
 */
export function isFirewallRedirectError(error) {
	return error && error.message && error.message.startsWith('FIREWALL_REDIRECT_');
}

/**
 * Check if an error is a specific vendor's redirect error
 * @param {Error} error - Error object to check
 * @param {string} vendorKey - Vendor key to check for (e.g., 'zscaler', 'cisco')
 * @returns {boolean} True if this is the specified vendor's redirect error
 */
export function isVendorRedirectError(error, vendorKey) {
	return error && error.message === `FIREWALL_REDIRECT_${vendorKey.toUpperCase()}`;
}

/**
 * Get vendor information from a firewall redirect error
 * @param {Error} error - Error object
 * @returns {Object|null} Vendor info or null if not a firewall redirect
 */
export function getVendorFromError(error) {
	if (!isFirewallRedirectError(error)) return null;

	const vendorKey = error.message.replace('FIREWALL_REDIRECT_', '').toLowerCase();
	return FIREWALL_VENDORS[vendorKey] || null;
}

/**
 * Handle firewall redirect errors gracefully
 * @param {Error} error - Error object
 * @param {Function} onFirewallRedirect - Callback for firewall redirects
 * @param {Function} onOtherError - Callback for other errors
 */
export function handleFirewallError(error, onFirewallRedirect, onOtherError) {
	if (isFirewallRedirectError(error)) {
		if (onFirewallRedirect) {
			onFirewallRedirect(error);
		}
	} else {
		if (onOtherError) {
			onOtherError(error);
		}
	}
}

// Legacy function for backward compatibility
export function isZscalerRedirectError(error) {
	return isVendorRedirectError(error, 'zscaler');
}

// Legacy function for backward compatibility
export function handleZscalerError(error, onZscalerRedirect, onOtherError) {
	if (isZscalerRedirectError(error)) {
		if (onZscalerRedirect) {
			onZscalerRedirect();
		}
	} else {
		if (onOtherError) {
			onOtherError(error);
		}
	}
}

/**
 * Get list of supported firewall vendors
 * @returns {Object} Object containing all supported vendors
 */
export function getSupportedVendors() {
	return { ...FIREWALL_VENDORS };
}

/**
 * Add a new firewall vendor configuration
 * @param {string} key - Vendor key
 * @param {Object} config - Vendor configuration
 */
export function addFirewallVendor(key, config) {
	if (!key || !config || !config.patterns || !config.name || !config.message) {
		throw new Error('Invalid vendor configuration. Required: key, patterns, name, message');
	}

	FIREWALL_VENDORS[key] = config;
	// Clear cache when adding new vendor
	vendorDetectionCache.clear();
}

export const sleep = (ms) => {
	return new Promise(resolve => setTimeout(resolve, ms));
}

// Constants for polling configuration
const POLLING_INTERVAL = 1000; // 1 second
const MAX_POLLING_ATTEMPTS = 60; // 1 minute timeout
const POLLING_TIMEOUT = 60000; // 60 seconds

// Corporate firewall/proxy vendor patterns
const FIREWALL_VENDORS = {
	zscaler: {
		patterns: ['gateway.zscaler.net', 'zscaler.net'],
		name: 'Zscaler',
		message: 'Corporate firewall is blocking this request. You will be redirected to accept the connection.'
	},
	cisco: {
		patterns: ['gateway.cisco.com', 'cisco.com/security', 'wsa.cisco.com'],
		name: 'Cisco WSA',
		message: 'Cisco Web Security Appliance is blocking this request. You will be redirected to accept the connection.'
	},
	fortinet: {
		patterns: ['fortinet.com', 'fortigate.com', 'forticlient.com'],
		name: 'Fortinet',
		message: 'Fortinet security gateway is blocking this request. You will be redirected to accept the connection.'
	},
	juniper: {
		patterns: ['juniper.net', 'juniper.com'],
		name: 'Juniper',
		message: 'Juniper security gateway is blocking this request. You will be redirected to accept the connection.'
	},
	bluecoat: {
		patterns: ['bluecoat.com', 'symantec.com/bluecoat'],
		name: 'Blue Coat',
		message: 'Blue Coat security gateway is blocking this request. You will be redirected to accept the connection.'
	},
	paloalto: {
		patterns: ['paloaltonetworks.com', 'paloalto.com'],
		name: 'Palo Alto Networks',
		message: 'Palo Alto security gateway is blocking this request. You will be redirected to accept the connection.'
	},
	checkpoint: {
		patterns: ['checkpoint.com', 'checkpoint.net'],
		name: 'Check Point',
		message: 'Check Point security gateway is blocking this request. You will be redirected to accept the connection.'
	},
	barracuda: {
		patterns: ['barracuda.com', 'barracudanetworks.com'],
		name: 'Barracuda',
		message: 'Barracuda security gateway is blocking this request. You will be redirected to accept the connection.'
	},
	forcepoint: {
		patterns: ['forcepoint.com', 'websense.com'],
		name: 'Forcepoint',
		message: 'Forcepoint security gateway is blocking this request. You will be redirected to accept the connection.'
	},
	generic: {
		patterns: ['gateway.', 'security.', 'firewall.', 'proxy.', 'filter.'],
		name: 'Corporate Security Gateway',
		message: 'Corporate security gateway is blocking this request. You will be redirected to accept the connection.'
	}
};

/**
 * Handle response based on content type
 * @param {Response} response - Fetch response object
 * @returns {Promise} Parsed response data
 */
function handleResponseByContentType(response) {
	const contentType = response.headers.get('content-type');

	if (contentType && contentType.includes('application/json')) {
		return response.json();
	} else if (contentType && contentType.includes('application/pdf')) {
		return response.blob();
	} else if (contentType && contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
		return response.blob();
	}

	return response.text();
}

// Cache for vendor detection results
const vendorDetectionCache = new Map();

/**
 * Detect which corporate firewall/proxy vendor is causing the redirect
 * @param {string} location - Redirect location URL
 * @returns {Object|null} Vendor info or null if not a known vendor
 */
function detectFirewallVendor(location) {
	if (!location) return null;

	// Check cache first
	if (vendorDetectionCache.has(location)) {
		return vendorDetectionCache.get(location);
	}

	// Check each vendor's patterns
	for (const [vendorKey, vendorInfo] of Object.entries(FIREWALL_VENDORS)) {
		for (const pattern of vendorInfo.patterns) {
			if (location.includes(pattern)) {
				const result = {
					key: vendorKey,
					name: vendorInfo.name,
					message: vendorInfo.message,
					location: location
				};

				// Cache the result
				vendorDetectionCache.set(location, result);
				return result;
			}
		}
	}

	// Cache negative result
	vendorDetectionCache.set(location, null);
	return null;
}

/**
 * Validate redirect URL for security
 * @param {string} url - URL to validate
 * @returns {boolean} True if URL is safe to redirect to
 */
function isValidRedirectUrl(url) {
	try {
		const urlObj = new URL(url);
		// Only allow HTTPS redirects or localhost for development
		return urlObj.protocol === 'https:' ||
			(urlObj.protocol === 'http:' && urlObj.hostname === 'localhost');
	} catch {
		return false;
	}
}

/**
 * Add cache-busting parameters to URL to avoid firewall caching issues
 * @param {string} url - Original URL
 * @returns {string} URL with cache-busting parameters
 */
function addCacheBusting(url) {
	const separator = url.includes('?') ? '&' : '?';
	const timestamp = Date.now();
	const random = Math.random().toString(36).substring(7);
	return `${url}${separator}_cb=${timestamp}&_r=${random}`;
}

/**
 * Check if request should use cache-busting (for corporate firewall compatibility)
 * @param {string} url - Request URL
 * @returns {boolean} Whether to use cache-busting
 */
function shouldUseCacheBusting(url) {
	// Use cache-busting for API endpoints that commonly trigger corporate firewalls
	const apiEndpoints = ['/apps', '/api/', '/auth/', '/login'];
	return apiEndpoints.some(endpoint => url.includes(endpoint));
}

/**
 * Poll for progress with timeout and error handling
 * @param {string} progressUrl - URL to poll for progress
 * @param {Object} params - Request parameters
 * @returns {Promise<Object>} Progress result
 */
async function pollForProgress(progressUrl, params) {
	let attempts = 0;
	const startTime = Date.now();

	while (attempts < MAX_POLLING_ATTEMPTS && (Date.now() - startTime) < POLLING_TIMEOUT) {
		try {
			await sleep(POLLING_INTERVAL);
			const progress = await fetch(progressUrl, params);

			if (!progress.ok) {
				throw new Error(`Progress check failed: ${progress.status} ${progress.statusText}`);
			}

			const progressData = await progress.json();

			if (progressData.ready) {
				return progressData;
			}

			attempts++;
		} catch (error) {
			console.error('Error during progress polling:', error);
			// Continue polling unless it's a critical error
			if (error.message.includes('Failed to fetch')) {
				attempts++;
				continue;
			}
			throw error;
		}
	}

	throw new Error(`Progress polling timed out after ${POLLING_TIMEOUT / 1000} seconds`);
}

/**
 * Fetch data
 *
 * @param {string} url
 * @param {Object} options
 * @param {string} [options.method] - Request method ( GET, POST, PUT, ... ).
 * @param {string} [options.payload] - Request body.
 * @param {Object} [options.headers]
 *
 * @returns {Promise}
 */
export function request(url, options = {}) {
	const config = {
		method: 'GET',
		...options,
	};
	const errors = [];

	if (!url) {
		errors.push('url');
	}

	if (!config.payload && config.method !== 'GET' && config.method !== 'DELETE') {
		errors.push('payload');
	}

	if (errors.length) {
		throw new Error(`Error! You must pass \`${errors.join('`, `')}\``);
	}

	// Add cache-busting for corporate firewall compatibility on problematic endpoints
	let finalUrl = url;
	if (shouldUseCacheBusting(url)) {
		finalUrl = addCacheBusting(url);
		console.log('Using cache-busting for corporate firewall compatibility:', finalUrl);
	}

	const headers = {
		Accept: 'application/json',
		'Content-Type': 'application/json',
		...config.headers,
		Authorization: localStorage.getItem('jwt_access_token') || '',
	};

	const params = {
		headers,
		method: config.method,
	};

	if (params.method !== 'GET') {
		params.body = JSON.stringify(config.payload);
	}

	return fetch(finalUrl, params).then(async response => {
		const contentType = response.headers.get('content-type');
		console.log("response.status", response.status);
		if (response.status > 299) {
			if (response.status === 401) {
				// For SSO, on 401 validate against SSO session
				let isSsoLogin = localStorage.getItem('isSsoLogin')
				console.log("isSsoLogin ????", isSsoLogin)
				if (isSsoLogin) {
					toast.info("Please try again, due to inactivity session is being restarted", { toastId: 'restart-session' })
					store.dispatch(ssoExpire());
					// Throw error to stop request chain - SilentSSO will handle the refresh
					const error = new ServerError('Session expired - SSO refresh in progress');
					error.status = 401;
					error.ssoRefreshPending = true;
					throw error;
				} else {
					console.log('session expired, login again')
					store.dispatch(logoutUser());
					// Throw error to stop request chain
					const error = new ServerError('Session expired');
					error.status = 401;
					throw error;
				}
			} else if (response.status === 307) {
				const location = response.headers.get('Location');

				if (!location) {
					throw new Error('307 redirect missing Location header');
				}

				// Check if this is a corporate firewall/proxy redirect
				const vendor = detectFirewallVendor(location);
				console.log("vendor ===== redirect", vendor);
				if (vendor) {
					console.log(`${vendor.name} redirect detected:`, location);
					console.warn(`${vendor.name} redirect detected:`, location);
					location = `/health`

					console.log('Redirect to health check URL for firewall acceptance:', location);
					console.warn('Redirect to health check URL for firewall acceptance:', location);
					window.location.href =`/health`

					/*
					// Validate redirect URL for security
					if (!isValidRedirectUrl(location)) {
						console.error('Invalid redirect URL detected:', location);
						throw new Error('Invalid redirect URL - potential security risk');
					}

					// Show vendor-specific notification
					toast.warning(
						vendor.message,
						{
							toastId: `${vendor.key}-redirect`,
							autoClose: 5000
						}
					);

					// Redirect to vendor's warning page
					window.location.href = location;

					// Return a special error that can be handled by calling code
					throw new Error(`FIREWALL_REDIRECT_${vendor.key.toUpperCase()}`);
					*/
				}

				// Handle regular progress polling (existing logic)
				console.log("Handling 307 redirect with progress polling");

				try {
					// Poll for progress with timeout and error handling
					const progress = await pollForProgress(location, params);

					if (!progress.fileUrl) {
						throw new Error('Progress completed but no file URL provided');
					}

					// Fetch the final result
					const finalResponse = await fetch(progress.fileUrl, params);

					if (!finalResponse.ok) {
						throw new Error(`Failed to fetch final result: ${finalResponse.status} ${finalResponse.statusText}`);
					}

					// Handle response based on content type
					return handleResponseByContentType(finalResponse);

				} catch (error) {
					console.error('Error handling 307 redirect:', error);
					throw new Error(`Progress polling failed: ${error.message}`);
				}
			} else {
				const error = new ServerError(response.statusText);
				error.status = response.status;

				if (contentType && contentType.includes('application/json')) {
					error.response = await response.json();
				} else {
					error.response = await response.text();
				}

				throw error.response;
			}
		} else {
			// Handle successful response based on content type
			return handleResponseByContentType(response);
		}
	});
}
